import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { useCallback } from 'react';

import { CheckboxDecorator } from '../../components/CheckboxDecorator/CheckboxDecorator';
import { Bem } from '../../service/Bem/Bem';

import './CreativeSizesList.css';
import * as keyset from './CreativeSizesList.i18n';
import { calculateFormatPreviewStyleSize } from './helpers';
import { CreativeSize } from './typings';

const i18n = i18nFactory(keyset);

export interface CreativeSizesListProps {
    sizes: CreativeSize[];
    selectedSize: number;
    onChangeSize: (value: number) => void;

    checkboxEnabled?: boolean;
    onCheckSize?: (value: number) => void;
}

interface CreativeSizesItemProps {
    size: CreativeSize;

    selected: boolean;
    onChangeSize: (value: number) => void;

    checkboxEnabled?: boolean;
    onCheckSize?: (value: number) => void;
    checked?: boolean;

    divRef?: React.RefObject<HTMLDivElement>;
}

const BLOCK = 'creative-sizes-list';

export class CreativeSizesList extends React.PureComponent<CreativeSizesListProps> {
    private static SizeItem = React.memo((props: CreativeSizesItemProps) => {
        const {
            size: { id, width, height, available },
            selected,
            onChangeSize,
            checkboxEnabled,
            checked,
            onCheckSize,
            divRef
        } = props;

        const name = `${width}x${height}`;

        const onClick = useCallback(() => onChangeSize(id), [id]);
        const onChecked = useCallback(() => {
            if (!onCheckSize) {
                return;
            }

            onCheckSize(id);
        }, [id]);

        return (
            <div
                className={Bem.cls(BLOCK, 'selector-item', {
                    selected
                })}
                ref={divRef}
            >
                <div
                    className={Bem.cls(BLOCK, 'content', {
                        'checkbox-enabled': Boolean(checkboxEnabled),
                        disabled: !checkboxEnabled && !available
                    })}
                    onClick={onClick}
                >
                    <div
                        className={Bem.cls(BLOCK, 'size-box')}
                        style={calculateFormatPreviewStyleSize(width, height)}
                    />
                    <div className={Bem.cls(BLOCK, 'size-name')}>{name}</div>
                </div>
                {checkboxEnabled && (
                    <CheckboxDecorator
                        cls={Bem.cls(BLOCK, 'selector-checkbox')}
                        size="s"
                        view="classic"
                        theme="normal"
                        item={id}
                        checked={checked}
                        onChecked={onChecked}
                    />
                )}
            </div>
        );
    });

    private selectedSize: React.RefObject<HTMLDivElement> = React.createRef();

    componentDidMount(): void {
        if (this.selectedSize.current) {
            this.selectedSize.current.scrollIntoView();
        }
    }

    render(): React.ReactNode {
        const { sizes, selectedSize, checkboxEnabled, onChangeSize, onCheckSize } = this.props;

        return (
            <div className={BLOCK}>
                <div className={Bem.cls(BLOCK, 'title')}>{i18n('sizes-title')}</div>
                <div className={Bem.cls(BLOCK, 'selector')}>
                    {sizes.map((size: CreativeSize) => (
                        <CreativeSizesList.SizeItem
                            key={size.id}
                            size={size}
                            selected={selectedSize === size.id}
                            onChangeSize={onChangeSize}
                            checkboxEnabled={checkboxEnabled}
                            onCheckSize={onCheckSize}
                            checked={size.checked}
                            divRef={selectedSize === size.id ? this.selectedSize : undefined}
                        />
                    ))}
                </div>
            </div>
        );
    }
}
